<template>
  <div class="wt2-make-new-wochit" :class="{ '-has-ai': hasAiFeatures }">
    <div class="-title-wrapper">
      <span class="-title wt2-text-2xl">
        <template v-if="existingStoryParams">
          <template v-if="isRegenerateStory"> Edit with AI </template>
          <template v-else> Build Timeline with AI </template>
        </template>
        <template v-else>
          {{ 'makeNewWochitDialog.startNewVideo' | culture }}
        </template>
      </span>
    </div>

    <div class="-form" v-if="!existingStoryParams">
      <div class="-form-inner">
        <wt-brands-dropdown
          :current-brand.sync="currentBrand"
          :enable-stash="true"
        />
        <div class="-aspect-ratios-wrapper">
          <wt-aspect-ratio-selector
            :aspect-ratios="aspectRatios"
            :default-aspect-ratio="defaultAspectRatio"
            @select="setAspectRatio"
          />
        </div>
      </div>
    </div>

    <div class="wt2-make-ai-story" v-if="hasAiFeatures">
      <div class="-ai-form">
        <div class="-ai-form-inner">
          <div class="-title" v-text="titleText" />

          <div class="-input-prompt-wrapper">
            <textarea
              class="-input-prompt -ai-settings"
              :class="{ '-active': aiParamsVisible }"
              v-model="prompt"
              ref="inputPrompt"
              @focus="aiPromptFocused = true"
              v-click-outside="onBlurPrompt"
              :disabled="processingAiRequest"
              @mouseenter="promptHovered = true"
              @mouseleave="promptHovered = false"
            />
            <div
              class="-prompt-examples"
              v-if="!aiPromptFocused && !promptLength"
            >
              <transition-group name="fade" mode="out-in" tag="span">
                <span
                  class="slider-item"
                  :key="example.id"
                  v-html="example.prompt"
                  v-show="example.id === currentAiExampleId"
                  v-for="example in activeAiPromptExamples"
                />
              </transition-group>
            </div>
            <div class="-template-icon-wrapper" v-if="!processingAiRequest">
              <wt-prompt-templates
                :prompt="prompt"
                :disabled="processingAiRequest"
                @template-selected="onTemplateSelected"
                :textarea-active="aiPromptFocused || promptHovered"
              />
            </div>
          </div>
          <div class="-prompt-length-error" v-if="promptTooLong">
            <span
              v-text="
                'Your Prompt is too long (' +
                promptLength.toLocaleString() +
                ' characters).  Max length is ' +
                maxPromptLength.toLocaleString() +
                ' characters.'
              "
            />
          </div>
          <div
            class="-settings -ai-settings"
            :class="{ '-visible': aiParamsVisible }"
          >
            <div>
              <Label>Language:</Label>
              <wt-dropdown
                type="normal"
                :items="sortedLanguageList"
                :num-items="Math.min(7, sortedLanguageList.length)"
                :current-item-id="language"
                :on-click-item="onChangeLanguage"
                :disabled="processingAiRequest"
              />
            </div>
            <div>
              <Label>Media Options:</Label>
              <wt-dropdown
                type="normal"
                :items="filteredVisualTypeList"
                :num-items="Math.min(7, filteredVisualTypeList.length)"
                :current-item-id="visualType"
                :on-click-item="onChangVisualType"
                :disabled="processingAiRequest"
              />
            </div>
            <div>
              <Label>Voice-over:</Label>
              <wt-voice-over-dropdown
                v-model="voiceId"
                :disabled="processingAiRequest"
                :language="language"
                :noVoiceOverMessage="
                  existingStoryParams && existingStoryParams.hasVoiceOverAssets
                    ? 'No New Voice-over'
                    : 'No Voice-over'
                "
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="-actions" v-tab-guard>
      <wt-button
        class="-min-width-unset"
        tabindex="0"
        type="transparent"
        :text="'makeNewWochitDialog.cancel' | culture"
        @click="closeModal"
        :disabled="processingAiRequest"
      />
      <wt-button
        :text="'makeNewWochitDialog.button' | culture"
        tabindex="-1"
        :type="isAiRequest ? 'ai' : 'primary'"
        :icon="isAiRequest ? 'wticon-magic-wand' : ''"
        @click="handleCreateAction"
        :processing="processingAiRequest"
        :disabled="processingAiRequest || (isAiRequest && !promptLength)"
      />
    </div>
  </div>
</template>

<script>
import app from 'app';
import { mapActions } from 'vuex';
import WtBrandsDropdown from '#/components/dropdowns/wtBrandsDropdown.vue';
import WtPromptTemplates from './wtPromptTemplates.component.vue';
import WtVoiceOverDropdown from '#/components/dropdowns/wtVoiceOverDropdown.vue';

export default {
  name: 'wtMakeNewWochit',
  components: { WtBrandsDropdown, WtPromptTemplates, WtVoiceOverDropdown },
  store() {
    return app.rootStore;
  },

  props: {
    closeModal: { type: Function, required: true },
    // used to identify where opened from
    page: { type: String, required: false },
    existingStoryParams: { type: Object, required: false },
  },

  data() {
    return {
      currentBrand: this.$store.getters['user/selectedOrganization'].brands[0],
      selectedAspectRatioKey: null,
      // lastAiVisualTypeStashKey: 'lastAiVT',
      refreshHomeStories: false,
      prompt: '',
      language: 'ENGLISH',
      visualType: 'EDITORIAL',
      voiceId: null,
      promptHovered: false,
      maxPromptLength: 50000,
      aiPromptFocused: false,
      aiPromptExamples: [
        {
          id: 0,
          prompt: 'Popular 90s hairstyles - use only multilines',
          duration: 5,
        },
        {
          id: 1,
          prompt: `Who was Princess Diana? Include information about her ties with the royal family, her impact, and a famous quote she said.`,
          duration: 6,
        },
        {
          id: 2,
          prompt: `Create a 4 scene video based on this article:<br/>https://mywebsite.com/my_article`,
          duration: 6,
        },
        {
          id: 3,
          prompt: `Time magazine recently crowned Taylor Swift as their Person of the Year Swift's "exceptional storytelling ability" earned her this laurel, marking her the first female to grace the cover twice since the accolade's inception in 1927....`,
          duration: 7,
        },
        {
          id: 4,
          prompt: `https://mywebsite.com/my_article`,
          duration: 5,
        },
        {
          id: 5,
          prompt: `Create a 5 scene video: "Is black pepper good for you?"<br/>Add an ending scene with a quote related to black pepper.`,
          duration: 5,
        },
        {
          id: 6,
          prompt: `Use the following script to create a video:<br/>
                Scene 1 - Title: "Is black pepper good for you?" Subtitle: "Let's find out!"<br/>
                Scene 2 - "Black pepper is good for you because it contains piperine."<br/>
                Scene 3 - ...`,
          duration: 6,
        },
        {
          id: 7,
          prompt: `Create a New Jersey weather forecast video using the data at: https://mywebsite.com/upcoming_forecast<br/>The video should contain 5 listicle scenes for each day.`,
          duration: 7,
        },
        {
          id: 8,
          prompt: `Video title: "Top 5 Vacation Resorts in Mexico".<br/>Use only the top 5 vacations from: https://mywebsite.com/top_10_mexico_vacation_resorts<br/>`,
          duration: 6,
        },
        {
          id: 9,
          prompt: `Create a video on Albert Einstein featuring:<br/>
                an introduction scene using a title, two multi-line biography scenes, five empty scenes, and a closing scene with a quote from Einstein.
                <br/>Pick one of his famous quotes from: https://mywebsite.com/einstein_quotes`,
          duration: 14,
        },
      ],
      aiPromptExamplesRegenerate: [
        {
          id: 0,
          prompt: 'Add a title scene at the beginning',
          duration: 3,
        },
        {
          id: 1,
          prompt: 'Add voiceover narration to all scenes',
          duration: 3,
        },
        {
          id: 2,
          prompt: 'Use listicles to describe the scenes',
          duration: 3,
        },
        {
          id: 3,
          prompt: 'Add English dubbing to scenes where people speak Spanish',
          duration: 5,
        },
        {
          id: 4,
          prompt: 'Add a conclusion scene',
          duration: 3,
        },
        {
          id: 5,
          prompt: 'Shorten scene 2 and extend scene 4 to 10 seconds',
          duration: 4,
        },
        {
          id: 6,
          prompt: 'Add background music and make the overall tone more upbeat',
          duration: 4,
        },
        {
          id: 7,
          prompt: 'Translate the video to Spanish',
          duration: 3,
        },
        {
          id: 8,
          prompt:
            'Update the video with latest data from:<br/>https://example.com/article',
          duration: 4,
        },
        {
          id: 9,
          prompt:
            'Use all the flower images in the Lightbox to tell a story about spring blooms',
          duration: 5,
        },
      ],
      currentAiExampleId: 0,
      promptExamplesTimeout: null,
      processingAiRequest: false,
      languageList: [],
    };
  },

  created() {
    this.languageList = app.AI_LANGUAGE_LIST;
    if (
      this.existingStoryParams &&
      !(
        this.existingStoryParams.emptyTimeline &&
        this.existingStoryParams.assetUUIDs.length === 0
      )
    ) {
      this.visualType = 'PLACEHOLDER';
    } else if (
      !this.filteredVisualTypeList.find((v) => v.id === this.visualType)
    ) {
      this.visualType = this.filteredVisualTypeList[0]?.id || 'PLACEHOLDER';
    }
    if (
      app.remoteStash.get('lastAiLanguage') &&
      this.languageList.find(
        (l) => l.id === app.remoteStash.get('lastAiLanguage')
      )
    ) {
      this.language = app.remoteStash.get('lastAiLanguage');
    }
    // if (
    //   app.remoteStash.get(this.lastAiVisualTypeStashKey) &&
    //   this.visualTypeList.find(
    //     (l) => l.id === app.remoteStash.get(this.lastAiVisualTypeStashKey)
    //   )
    // ) {
    //   this.visualType = app.remoteStash.get(this.lastAiVisualTypeStashKey);
    // }
  },

  mounted() {
    // shuffle aiPromptExamples:
    this.aiPromptExamples.sort(() => Math.random() - 0.5);
    this.aiPromptExamplesRegenerate.sort(() => Math.random() - 0.5);
    this.setNextPromptExample();
  },

  beforeDestroy() {
    clearTimeout(this.promptExamplesTimeout);
    if (this.refreshHomeStories) {
      this.refreshDashboardStories();
    }
  },

  computed: {
    isRegenerateStory() {
      return this.existingStoryParams?.regenerateStory;
    },
    titleText() {
      return this.isRegenerateStory
        ? 'Describe the changes you want and AI will edit your video using the existing timeline & Lightbox assets:'
        : this.existingStoryParams
        ? 'Prompt:'
        : 'Speed up things by creating a draft from a prompt or URL:';
    },
    activeAiPromptExamples() {
      return this.isRegenerateStory
        ? this.aiPromptExamplesRegenerate
        : this.aiPromptExamples;
    },
    isAiRequest() {
      return this.promptLength || !!this.existingStoryParams;
    },

    aiParamsVisible() {
      return (
        this.hasAiFeatures &&
        (this.promptLength ||
          this.aiPromptFocused ||
          !!this.existingStoryParams)
      );
    },

    sortedLanguageList() {
      return [...this.languageList].sort((a, b) =>
        a.label.localeCompare(b.label)
      );
    },

    hasAiFeatures() {
      return (
        tk.context.organization.premiumFeatures &&
        tk.context.organization.premiumFeatures.includes('AI_PROMPT')
      );
    },
    promptLength() {
      return this.prompt?.trim().length;
    },
    promptTooLong() {
      return this.promptLength > this.maxPromptLength;
    },
    promptLengthError() {
      return this.promptTooLong || this.promptLength === 0;
    },
    availableProviders() {
      const org = tk.context.organization;
      const editorialProviders = org.providerMetadatas || [];
      const creativeProviders = (org.providerDynamicMetadatas || []).filter(
        (p) => p.id != 'kaltura'
      );
      return {
        editorial: editorialProviders.length > 0,
        creative: creativeProviders.length > 0,
      };
    },
    filteredVisualTypeList() {
      const enabled =
        this.existingStoryParams &&
        !(
          this.existingStoryParams.emptyTimeline &&
          this.existingStoryParams.assetUUIDs.length === 0
        )
          ? [
              { id: 'PLACEHOLDER', label: 'Use only Lightbox assets' },
              { id: 'EDITORIAL', label: 'Add editorial footage as needed' },
              {
                id: 'CREATIVE',
                label: 'Add creative footage as needed',
              },
              {
                id: 'EDITORIAL_AND_CREATIVE',
                label: 'Add editorial and creative footage as needed',
              },
            ]
          : [
              { id: 'EDITORIAL', label: 'Editorial Only' },
              {
                id: 'CREATIVE',
                label: 'Creative Only',
              },
              {
                id: 'EDITORIAL_AND_CREATIVE',
                label: 'Editorial & Creative',
              },
              { id: 'PLACEHOLDER', label: 'No Media (Use Placeholders)' },
            ];
      return enabled.filter((option) => {
        if (option.id === 'EDITORIAL') return this.availableProviders.editorial;
        if (option.id === 'CREATIVE') return this.availableProviders.creative;
        if (option.id === 'EDITORIAL_AND_CREATIVE') {
          return (
            this.availableProviders.editorial &&
            this.availableProviders.creative
          );
        }
        return true;
      });
    },
    isFormValid() {
      return !this.promptLengthError;
    },
    aspectRatios() {
      const rv = {};

      Object.keys(app.ASPECT_RATIOS).forEach((arKey) => {
        if (
          this.currentBrand.themes.find((theme) => theme.aspectRatio === arKey)
        ) {
          rv[arKey] = app.ASPECT_RATIOS[arKey];
        }
      });

      return rv;
    },

    defaultAspectRatio() {
      let rv = app.DEFAULT.ASPECT_RATIO;
      const aspectRatioKeys = Object.keys(this.aspectRatios);
      const last =
        app.remoteStash.get('lastAspectRatio') || app.DEFAULT.ASPECT_RATIO;

      if (last in this.aspectRatios) {
        rv = last;
      } else if (aspectRatioKeys.length) {
        rv = aspectRatioKeys[0];
      }

      return rv;
    },
  },

  watch: {
    promptHovered() {
      if (this.promptHovered) {
        clearTimeout(this.promptExamplesTimeout);
      } else {
        this.setNextPromptExample(false);
      }
    },
  },

  methods: {
    ...mapActions(['startStoryFrom']),

    refreshDashboardStories() {
      const routePath = app.rootStore.getters.routePath;
      if (routePath === 'dashboard/home') {
        app.rootStore.dispatch('fetchHomeStories', { bReset: true });
      } else if (routePath === 'dashboard/inProgress') {
        app.rootStore.dispatch('fetchStories', { bReset: true });
      }
    },

    onTemplateSelected(templateContent) {
      const textarea = this.$refs.inputPrompt;
      if (!textarea) return;

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const before = this.prompt.substring(0, start);
      const after = this.prompt.substring(end);

      // Insert template content at cursor position
      this.prompt = before + templateContent + after;

      // Restore cursor position after inserted content
      this.$nextTick(() => {
        const newPosition = start + templateContent.length;
        textarea.setSelectionRange(newPosition, newPosition);
        textarea.focus();
      });
    },

    setNextPromptExample(nextPrompt = true) {
      if (nextPrompt) {
        const examplesLength = this.activeAiPromptExamples.length;
        const currentIndex = this.activeAiPromptExamples.findIndex(
          (ex) => ex.id === this.currentAiExampleId
        );
        const nextIndex = (currentIndex + 1) % examplesLength;
        this.currentAiExampleId = this.activeAiPromptExamples[nextIndex].id;
      }
      const duration = nextPrompt
        ? this.activeAiPromptExamples.find(
            (ex) => ex.id === this.currentAiExampleId
          ).duration
        : 1;
      this.promptExamplesTimeout = setTimeout(() => {
        this.setNextPromptExample();
      }, duration * 1000);
    },

    setAspectRatio(newAspectRatio) {
      this.selectedAspectRatioKey = newAspectRatio;
      app.remoteStash.set('lastAspectRatio', newAspectRatio);
    },
    onChangeLanguage(language) {
      app.remoteStash.set('lastAiLanguage', language);
      this.language = language;
    },
    onChangVisualType(visualType) {
      // app.remoteStash.set(this.lastAiVisualTypeStashKey, visualType);
      this.visualType = visualType;
    },
    handleCreateAction() {
      if (this.isAiRequest) {
        this.onCreateGen2();
      } else {
        this.onCreateBlankVideo();
      }
    },
    focusAndClearPrompt() {
      this.prompt = '';
      if (this.$refs.inputPrompt) {
        this.$refs.inputPrompt.focus();
      }
    },
    onBlurPrompt(e) {
      let el = e.target;
      let found = false;
      while (el) {
        if (el.classList.contains('-ai-settings')) {
          found = true;
          break;
        }
        el = el.parentElement;
      }
      if (!found) {
        this.aiPromptFocused = false;
      }
    },

    onCreateGen2: _.debounce(
      function () {
        if (!this.existingStoryParams) {
          this.processingAiRequest = true;
          const theme = this.currentBrand.themes.find(
            (theme) => theme.aspectRatio === this.selectedAspectRatioKey
          );
          const payload = {
            videoPrompt: this.prompt,
            language: this.language,
            visualType: this.visualType,
            programmerId: this.currentBrand.programmerId,
            voiceId: this.voiceId,
            brandId: this.currentBrand.brandId,
            themeId: theme.themeId,
            aspectRatio: this.selectedAspectRatioKey,
          };
          this.$store
            .dispatch('aiApiRequest', payload)
            .then((response) => {
              this.refreshHomeStories = true;
              app.aiTasksController.addTask(response.threadId);
              app.analytics('generateAiStoryGen2', {
                source: 'button',
                ...payload,
              });
              app.dialog
                .confirm({
                  type: app.dialog.types.CONFIRM_3,
                  title: 'Create a Video from Prompt',
                  description: `We are preparing your draft.
Expect it in your Drafts folder in a few minutes!`,
                  buttonNoText: 'Create another one',
                  buttonYesText: 'Close',
                  primaryBtnType: 'ai',
                  titleColorClass: '-ai',
                })
                .then((val) => {
                  if (val?.action) {
                    this.closeModal();
                  } else {
                    this.focusAndClearPrompt();
                  }
                })
                .catch(() => {
                  this.focusAndClearPrompt();
                })
                .finally(() => {
                  this.processingAiRequest = false;
                });
            })
            .catch(() => {
              this.processingAiRequest = false;
            });
          return;
        }

        const otherAssetsPromise = $.Deferred();
        if (this.existingStoryParams.hasOtherAssetsInLightbox) {
          app.dialog
            .confirm({
              type: app.dialog.types.CONFIRM_3,
              title: 'Not all assets are compatible',
              description:
                'Build with AI (Beta) processes only images and videos from the Lightbox.<br/>Narrations and other media types will be removed.',
              buttonYesText: 'Got It, Continue',
              primaryBtnType: 'ai',
              titleColorClass: '-ai',
            })
            .then((data) => {
              if (data.action) {
                otherAssetsPromise.resolve();
              } else {
                otherAssetsPromise.reject();
              }
            });
        } else {
          otherAssetsPromise.resolve();
        }
        otherAssetsPromise.promise().then(() => {
          this.processingAiRequest = true;

          this.prepareLockForAI(this.existingStoryParams.storyId, this.prompt)
            .then(() => {
              this.$store
                .dispatch('aiApiRequest', {
                  storyId: this.existingStoryParams.storyId,
                  brandId: this.existingStoryParams.brandId,
                  themeId: this.existingStoryParams.themeId,
                  programmerId: this.existingStoryParams.programmerId,
                  videoPrompt: this.prompt,
                  language: this.language,
                  visualType: this.visualType,
                  voiceId: this.voiceId,
                  assetUUIDs: this.existingStoryParams.assetUUIDs || [],
                  regenerateStory: this.isRegenerateStory,
                  aspectRatio: this.existingStoryParams.aspectRatio,
                })
                .then((response) => {
                  app.aiTasksController.addTask(response.threadId);
                  app.analytics('clickedGenerateAiTimeline', {
                    source: 'button',
                    prompt: this.prompt,
                    language: this.language,
                    visualType: this.visualType,
                    voiceId: this.voiceId,
                    assetUUIDs: this.existingStoryParams.assetUUIDs || [],
                    aspectRatio: this.selectedAspectRatioKey,
                    themeId: this.existingStoryParams.themeId,
                    programmerId: this.existingStoryParams.programmerId,
                    brandId: this.existingStoryParams.brandId,
                    storyId: this.existingStoryParams.storyId,
                  });
                  // exit story
                  app.dialog
                    .confirm({
                      type: app.dialog.types.CONFIRM_1,
                      title: 'Building Timeline with AI',
                      description: `We are preparing your draft.
Expect it in your Drafts folder in a few minutes!`,
                      buttonYesText: 'Close',
                      primaryBtnType: 'ai',
                      titleColorClass: '-ai',
                    })
                    .then(() => {
                      app.state.enableNavigation();
                      app.navigateToDashboard();
                    });
                })
                .catch(() => {
                  app.state.enableNavigation();
                  app.navigateToDashboard();
                  this.processingAiRequest = false;
                });
            })
            .catch(() => {
              this.processingAiRequest = false;
              app.dialog.error({
                type: app.dialog.types.ERROR_1,
                title: 'Failed to unlock story',
                description: 'Please refresh the page and try again later.',
                showContactCsm: true,
              });
            });
        });
      },
      app.TIME.MENTAL.λ,
      { leading: true, trailing: false }
    ),

    prepareLockForAI: function (storyId, title) {
      return new Promise((resolve, reject) => {
        app.storyController
          .prepareLockForAI({
            storyId,
            title,
            changeTitle: !this.isRegenerateStory,
          })
          .then(resolve)
          .catch(reject);
      });
    },

    onCreateBlankVideo: _.debounce(
      function () {
        app.analytics('clickedStartNewVideo', {
          source: 'button',
          page: this.page || 'unknown',
          aiPrompt: !!this.isAiRequest,
        });
        const theme = this.currentBrand.themes.find(
          (theme) => theme.aspectRatio === this.selectedAspectRatioKey
        );
        this.startStoryFrom({
          programmerId: this.currentBrand.programmerId,
          brandId: this.currentBrand.brandId,
          themeId: theme.themeId,
          aspectRatio: this.selectedAspectRatioKey,
        });
      },
      app.TIME.MENTAL.λ,
      { leading: true, trailing: false }
    ),
  },
};
</script>

<style lang="scss" scoped>
@import 'style_guide/_sg_variables.scss';

.wt2-make-new-wochit {
  width: 500px;
  &.-has-ai {
    width: 625px;
  }
  .-form {
    display: flex;
    justify-content: center;
    max-height: 80vh;
  }

  .-form-inner {
    width: 400px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
  }

  padding-top: 40px;
  height: 100%;
  position: relative;

  .-title-wrapper {
    display: flex;
    align-items: center;
    flex-direction: column;

    .-title {
      margin-bottom: 20px;
      position: relative;
    }

    .-back {
      position: absolute;
      left: -80px;
      top: 50%;
      transform: translateY(-50%);
      color: $color-primary;
      font-size: 20px;
      cursor: pointer;
      &:hover {
        color: $color-blue-d;
      }
    }
  }

  .wt-brands-dropdown {
    margin-bottom: 20px;
    width: 300px;
  }

  .-aspect-ratios-wrapper {
    margin: 0 auto 20px auto;
  }

  .-actions {
    width: 100%;
    display: flex;
    justify-content: flex-end;
    margin-top: 40px;
  }
}

.wt2-make-ai-story {
  .-title {
    margin: 10px 0 20px 0;
    font-size: 16px;
  }
  .-ai-form {
    display: flex;
    justify-content: center;
    flex-grow: 1;
  }

  .-ai-form-inner {
    width: 100%;
    padding: 0 40px;
  }

  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;

  .-title-wrapper {
    display: flex;
    align-items: center;
    flex-direction: column;

    .-title {
      margin-bottom: 20px;
      position: relative;
      font-weight: 500;
    }

    .-back {
      position: absolute;
      left: -80px;
      top: 50%;
      transform: translateY(-50%);
      color: $color-primary;
      font-size: 20px;
      cursor: pointer;
      &:hover {
        color: $color-blue-d;
      }
    }
  }

  .-input-prompt-wrapper {
    position: relative;

    .-input-prompt {
      width: 100%;
      height: 145px;
      resize: none;
      padding: 15px;
      font-size: 16px;
      border: none;
      background-color: rgba(0, 0, 0, 0.05);
      border-radius: 0;
      transition: height 0.2s ease-in;
      &.-active {
        height: 160px;
      }
    }

    .-prompt-examples {
      position: absolute;
      top: 0;
      left: 0;
      padding: 15px;
      font-size: 16px;
      color: rgba(0, 0, 0, 0.5);
      pointer-events: none;
      overflow: hidden;
      height: 100%;
      width: 100%;

      .slider-item {
        display: inline-block;

        &.fade-enter-active,
        &.fade-leave-active {
          transition: opacity 0.3s;
        }

        &.fade-enter-active {
          transition-delay: 0.45s;
        }

        &.fade-enter,
        &.fade-leave-to {
          opacity: 0;
        }
      }
    }

    .-template-icon-wrapper {
      position: absolute;
      bottom: 4px;
      right: 4px;
      z-index: 10;
      animation: fadeIn 0.2s ease-in;
    }

    @keyframes fadeIn {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }
  }

  .-settings {
    width: 310px;
    margin-top: 10px;
    display: flex;
    flex-direction: column;
    max-height: 0;
    opacity: 0;
    pointer-events: none;
    transition: max-height 0.2s ease-in;
    &.-visible {
      max-height: 180px;
      opacity: 1;
      pointer-events: auto;
    }
    > div {
      display: flex;
      flex-direction: row;
      align-items: center;
      label {
        min-width: 110px;
        font-weight: 400;
      }
    }
  }
}

.-disabled-media-options {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.5);
  margin: 15px;
  //disable select:
  pointer-events: none;
}
</style>
